/**
 * Inline workflow action panels for ScheduleDetailModal.
 *
 * Renders action buttons (Accept, Decline, Propose Date, Propose Qty)
 * and inline forms when an action is active. Extracted from ScheduleDetailModal
 * to keep both files under 500 lines.
 */
import { useState } from 'react';
import { Check, XCircle, CalendarDays, Hash, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import type { POLineSchedule } from '../../lib/api/types/purchasing';

interface ScheduleDetailActionsProps {
  schedule: POLineSchedule;
  activeAction: string | null;
  setActiveAction: (action: string | null) => void;
  canShowWorkflowActions: boolean;
  canShowProposeChange: boolean;
  onWorkflowAction: (action: string, data: Record<string, unknown>) => Promise<void>;
  onToggleHold: (reason?: string) => Promise<void>;
}

export default function ScheduleDetailActions({
  schedule,
  activeAction,
  setActiveAction,
  canShowWorkflowActions,
  canShowProposeChange,
  onWorkflowAction,
  onToggleHold,
}: ScheduleDetailActionsProps) {
  const [formReason, setFormReason] = useState('');
  const [formComments, setFormComments] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formQty, setFormQty] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const resetForm = () => {
    setFormReason('');
    setFormComments('');
    setFormDate('');
    setFormQty('');
  };

  const openAction = (action: string) => {
    resetForm();
    setActiveAction(action);
  };

  const closeAction = () => {
    resetForm();
    setActiveAction(null);
  };

  const runAction = async (action: string, data: Record<string, unknown>) => {
    setActionLoading(true);
    try {
      await onWorkflowAction(action, data);
      resetForm();
    } catch {
      // Error already handled in parent
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccept = () => runAction('accept', { comments: formComments || undefined });

  const handleDecline = () => {
    if (!formReason.trim()) { toast.error('Reason is required'); return; }
    runAction('decline', { reason: formReason, comments: formComments || undefined });
  };

  const handleProposeDate = () => {
    if (!formDate || !formReason.trim()) { toast.error('Date and reason are required'); return; }
    runAction('propose_date', { proposed_date: formDate, reason: formReason, comments: formComments || undefined });
  };

  const handleProposeQty = () => {
    if (!formQty || !formReason.trim()) { toast.error('Quantity and reason are required'); return; }
    runAction('propose_qty', { proposed_quantity: Number(formQty), reason: formReason, comments: formComments || undefined });
  };

  const handleHold = async () => {
    if (!formReason.trim()) { toast.error('Reason is required'); return; }
    setActionLoading(true);
    try {
      await onToggleHold(formReason);
      closeAction();
    } catch {
      // handled in parent
    } finally {
      setActionLoading(false);
    }
  };

  const showAnyAction = canShowWorkflowActions || canShowProposeChange;
  if (!showAnyAction && !schedule.on_hold) return null;

  return (
    <div className="mt-6 space-y-4">
      {/* Action Buttons */}
      {(showAnyAction || !schedule.on_hold) && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Actions</h3>
          <div className="flex flex-wrap gap-2">
            {canShowWorkflowActions && (
              <>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => openAction('accept')}
                >
                  <Check className="h-4 w-4 mr-1" /> Accept
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => openAction('decline')}
                >
                  <XCircle className="h-4 w-4 mr-1" /> Decline
                </Button>
              </>
            )}
            {canShowProposeChange && (
              <>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => openAction('propose_date')}
                >
                  <CalendarDays className="h-4 w-4 mr-1" /> Propose Date
                </Button>
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => openAction('propose_qty')}
                >
                  <Hash className="h-4 w-4 mr-1" /> Propose Qty
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Inline Action Forms */}
      {activeAction === 'accept' && (
        <ActionCard title="Accept Delivery Date" onCancel={closeAction}>
          <div>
            <Label htmlFor="accept-comments">Comments (optional)</Label>
            <Textarea id="accept-comments" value={formComments} onChange={(e) => setFormComments(e.target.value)} placeholder="Any additional comments..." />
          </div>
          <ActionFooter loading={actionLoading} onCancel={closeAction} onSubmit={handleAccept} label="Accept" variant="green" />
        </ActionCard>
      )}

      {activeAction === 'decline' && (
        <ActionCard title="Decline Delivery Date" onCancel={closeAction}>
          <div>
            <Label htmlFor="decline-reason">Reason *</Label>
            <Textarea id="decline-reason" value={formReason} onChange={(e) => setFormReason(e.target.value)} placeholder="Reason for declining..." />
          </div>
          <div>
            <Label htmlFor="decline-comments">Comments (optional)</Label>
            <Textarea id="decline-comments" value={formComments} onChange={(e) => setFormComments(e.target.value)} placeholder="Additional comments..." />
          </div>
          <ActionFooter loading={actionLoading} onCancel={closeAction} onSubmit={handleDecline} label="Decline" variant="red" />
        </ActionCard>
      )}

      {activeAction === 'propose_date' && (
        <ActionCard title="Propose Alternative Date" onCancel={closeAction}>
          <div>
            <Label htmlFor="propose-date">Proposed Date *</Label>
            <Input id="propose-date" type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="propose-date-reason">Reason *</Label>
            <Textarea id="propose-date-reason" value={formReason} onChange={(e) => setFormReason(e.target.value)} placeholder="Reason for date change..." />
          </div>
          <div>
            <Label htmlFor="propose-date-comments">Comments (optional)</Label>
            <Textarea id="propose-date-comments" value={formComments} onChange={(e) => setFormComments(e.target.value)} placeholder="Additional comments..." />
          </div>
          <ActionFooter loading={actionLoading} onCancel={closeAction} onSubmit={handleProposeDate} label="Propose Date" variant="blue" />
        </ActionCard>
      )}

      {activeAction === 'propose_qty' && (
        <ActionCard title="Propose Quantity Change" onCancel={closeAction}>
          <div>
            <Label htmlFor="propose-qty">Proposed Quantity *</Label>
            <Input id="propose-qty" type="number" min="0" value={formQty} onChange={(e) => setFormQty(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="propose-qty-reason">Reason *</Label>
            <Textarea id="propose-qty-reason" value={formReason} onChange={(e) => setFormReason(e.target.value)} placeholder="Reason for quantity change..." />
          </div>
          <div>
            <Label htmlFor="propose-qty-comments">Comments (optional)</Label>
            <Textarea id="propose-qty-comments" value={formComments} onChange={(e) => setFormComments(e.target.value)} placeholder="Additional comments..." />
          </div>
          <ActionFooter loading={actionLoading} onCancel={closeAction} onSubmit={handleProposeQty} label="Propose Qty" variant="purple" />
        </ActionCard>
      )}

      {activeAction === 'hold' && (
        <ActionCard title="Put Schedule On Hold" onCancel={closeAction}>
          <div>
            <Label htmlFor="hold-reason">Reason *</Label>
            <Textarea id="hold-reason" value={formReason} onChange={(e) => setFormReason(e.target.value)} placeholder="Reason for hold..." />
          </div>
          <ActionFooter loading={actionLoading} onCancel={closeAction} onSubmit={handleHold} label="Put On Hold" variant="orange" />
        </ActionCard>
      )}
    </div>
  );
}

// ---------- Sub-components ----------

interface ActionCardProps {
  title: string;
  onCancel: () => void;
  children: React.ReactNode;
}

function ActionCard({ title, children }: ActionCardProps) {
  return (
    <div className="border border-border rounded-lg p-4 bg-muted/20">
      <h4 className="text-sm font-semibold mb-3">{title}</h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

const VARIANT_CLASSES: Record<string, string> = {
  green: 'bg-green-600 hover:bg-green-700 text-white',
  red: 'bg-red-600 hover:bg-red-700 text-white',
  blue: 'bg-blue-600 hover:bg-blue-700 text-white',
  purple: 'bg-purple-600 hover:bg-purple-700 text-white',
  orange: 'bg-orange-600 hover:bg-orange-700 text-white',
};

interface ActionFooterProps {
  loading: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  label: string;
  variant: string;
}

function ActionFooter({ loading, onCancel, onSubmit, label, variant }: ActionFooterProps) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <Button variant="outline" size="sm" onClick={onCancel} disabled={loading}>Cancel</Button>
      <Button size="sm" className={VARIANT_CLASSES[variant] || ''} onClick={onSubmit} disabled={loading}>
        {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
        {label}
      </Button>
    </div>
  );
}
